import { Field, Formik, yupToFormErrors } from 'formik'
import React from 'react'

import { Dashboard } from '@components/common/Dashboard/Dashboard'
import { PageLayout } from '@components/common/PageLayout'
import { LeavingGuard } from '@components/utils/LeavingGuard'
import {
  BackButton,
  Checkbox,
  Fieldset,
  HeadContent,
  Loading,
  SaveShortcutListener,
  Subsection,
} from '@fourthwall/components'
import { ConfirmBar } from '@modules/Dashboard/modules/ConfirmBar'

import { ProductFiles } from '../ProductFiles/ProductFiles'
import { getTotalDigitalFilesSize } from '../SingleProduct/SingleProduct.utils'
import { SoundScan } from './components/SoundScan'
import { PRODUCT_SETTINGS_VIEW_TITLE, getValidationSchema } from './consts'
import * as S from './styled'
import type { FormValues, ProductSettingsViewProps } from './types'

export const ProductSettingsView = ({
  backButtonProps,
  initialValues,
  productData,
  productName = '',
  isLoading,
  isSubmitting,
  isFormEditable,
  digitalItemsEditable,
  creatorDeclaredCostEditable,
  maxDigitalFilesSize,
  isFreeProduct,
  withMembersOnly,
  onSubmit,
}: ProductSettingsViewProps) => {
  if (!productData) return null

  const getIsTotalDigitalFilesSizeExceeded = (values: FormValues) => {
    if (!maxDigitalFilesSize) return false

    const totalSize = getTotalDigitalFilesSize(values.digitalItems)

    return totalSize > maxDigitalFilesSize
  }

  return (
    <>
      <HeadContent title={`${PRODUCT_SETTINGS_VIEW_TITLE} \u2013 ${productName}`} />
      <Loading isLoading={isLoading}>
        <PageLayout>
          <Dashboard.Section
            name={PRODUCT_SETTINGS_VIEW_TITLE}
            topContent={<BackButton {...backButtonProps} />}
          >
            <Formik<FormValues>
              initialValues={initialValues}
              validate={(values) => {
                const schema = getValidationSchema({
                  isTotalDigitalFilesSizeExceeded: getIsTotalDigitalFilesSizeExceeded(values),
                })

                try {
                  schema.validateSync(values, { abortEarly: false })
                } catch (err) {
                  return yupToFormErrors(err)
                }
              }}
              enableReinitialize
              onSubmit={onSubmit}
            >
              {({ values, isValid, dirty, submitForm, resetForm }) => (
                <>
                  <SoundScan
                    isSoundScanProduct={values.isSoundScanProduct}
                    disabled={!isFormEditable}
                  />

                  <Subsection title="Require phone number from buyers">
                    <Field
                      component={Checkbox}
                      name="requirePhoneNumberFromBuyer"
                      label="Require buyers to add a phone number during checkout"
                    />
                  </Subsection>

                  {creatorDeclaredCostEditable && (
                    <Subsection title="Product costs">
                      <Field
                        component={Checkbox}
                        name="isCreatorDeclaredCostEnabled"
                        label="Set a product cost for your product so you can track your profits"
                        description="You can now enter product costs on the edit product page"
                      />
                    </Subsection>
                  )}

                  {digitalItemsEditable && (
                    <Subsection title="Digital download">
                      <Fieldset>
                        <Field
                          component={Checkbox}
                          name="digitalItemsEnabled"
                          label="Add digital download to this product"
                          disabled={!isFormEditable}
                        />

                        {values.digitalItemsEnabled && (
                          <Fieldset.Row inner>
                            <S.FileListWrapper>
                              <S.SeparatorLine />
                              <ProductFiles
                                maxSize={maxDigitalFilesSize}
                                offerId={productData.id}
                                files={values.digitalItems}
                                withMembersOnly={withMembersOnly}
                                isFormEditable={isFormEditable}
                                isTotalDigitalFilesSizeExceeded={getIsTotalDigitalFilesSizeExceeded(
                                  values,
                                )}
                                isFreeProduct={isFreeProduct}
                              />
                            </S.FileListWrapper>
                          </Fieldset.Row>
                        )}
                      </Fieldset>
                    </Subsection>
                  )}
                  <SaveShortcutListener disabled={!dirty} onSave={submitForm} />
                  <ConfirmBar
                    isOpen={dirty}
                    isLoading={
                      isSubmitting || values.digitalItems.some(({ isUploading }) => isUploading)
                    }
                    onConfirm={submitForm}
                    onCancel={resetForm}
                  />
                  <LeavingGuard when={dirty} isValid={isValid} onSave={submitForm} />
                </>
              )}
            </Formik>
          </Dashboard.Section>
        </PageLayout>
      </Loading>
    </>
  )
}
